import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Linkedin, 
  Mail, 
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertTriangle,
  FileCheck,
  FileX,
  Lock,
  LogOut,
  KeyRound
} from "lucide-react";

const formatCurrency = (value) => {
  if (!value) return "₹0";
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)} L`;
  }
  return `₹${value.toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const VerificationBadge = ({ verified, label }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${
    verified 
      ? 'bg-green-100 text-green-700 border border-green-200' 
      : 'bg-gray-100 text-gray-500 border border-gray-200'
  }`}>
    {verified ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
    {label}
  </span>
);

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    verified: 'bg-green-100 text-green-700 border-green-200',
    rejected: 'bg-red-100 text-red-700 border-red-200'
  };
  
  const icons = {
    pending: <Clock className="h-3 w-3" />,
    verified: <CheckCircle className="h-3 w-3" />,
    rejected: <XCircle className="h-3 w-3" />
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase tracking-wider border ${styles[status] || styles.pending}`}>
      {icons[status] || icons.pending}
      {status}
    </span>
  );
};

const SubmissionCard = ({ submission, onVerify, onReject, isExpanded, onToggle }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await (action === 'verify' ? onVerify : onReject)(submission.placement_id, notes);
      setNotes('');
    } finally {
      setLoading(false);
    }
  };

  const { verification_layers } = submission;

  return (
    <div className="border border-border bg-background" data-testid={`submission-${submission.placement_id}`}>
      {/* Header Row */}
      <div 
        className="p-4 cursor-pointer hover:bg-secondary transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <StatusBadge status={submission.verification_status} />
            <div>
              <h3 className="font-medium">{submission.company} - {submission.role}</h3>
              <p className="text-sm text-muted-foreground">
                {submission.college_name} · {submission.program_name} · {submission.year}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono font-bold">{formatCurrency(submission.total_ctc)}</span>
            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </div>

        {/* Quick verification indicators */}
        <div className="flex gap-2 mt-3">
          <VerificationBadge 
            verified={verification_layers?.linkedin?.verified} 
            label="LinkedIn" 
          />
          <VerificationBadge 
            verified={verification_layers?.company_email?.verified} 
            label="Company Email" 
          />
          <VerificationBadge 
            verified={verification_layers?.college_email?.verified} 
            label="College Email" 
          />
          {submission.is_ppo && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200 rounded">
              PPO
            </span>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border p-4 bg-secondary">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Submission Details */}
            <div className="space-y-4">
              <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Submission Details
              </h4>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-medium capitalize">{submission.placement_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Submitted:</span>
                  <span>{formatDate(submission.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">User ID:</span>
                  <span className="font-mono text-xs">{submission.user_id}</span>
                </div>
              </div>

              {/* Salary Breakdown */}
              {submission.placement_type === 'placement' && submission.salary_breakdown && (
                <div className="mt-4 p-3 bg-background border border-border">
                  <h5 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    Salary Breakdown
                  </h5>
                  <div className="space-y-1 text-sm font-mono">
                    <div className="flex justify-between">
                      <span>Fixed:</span>
                      <span>{formatCurrency(submission.salary_breakdown.fixed)}</span>
                    </div>
                    {submission.salary_breakdown.variable > 0 && (
                      <div className="flex justify-between">
                        <span>Variable:</span>
                        <span>{formatCurrency(submission.salary_breakdown.variable)}</span>
                      </div>
                    )}
                    {submission.salary_breakdown.esops > 0 && (
                      <div className="flex justify-between">
                        <span>ESOPs:</span>
                        <span>{formatCurrency(submission.salary_breakdown.esops)}</span>
                      </div>
                    )}
                    {submission.salary_breakdown.joining_bonus > 0 && (
                      <div className="flex justify-between">
                        <span>Joining Bonus:</span>
                        <span>{formatCurrency(submission.salary_breakdown.joining_bonus)}</span>
                      </div>
                    )}
                    {submission.salary_breakdown.relocation > 0 && (
                      <div className="flex justify-between">
                        <span>Relocation:</span>
                        <span>{formatCurrency(submission.salary_breakdown.relocation)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-border font-bold">
                      <span>Total CTC:</span>
                      <span>{formatCurrency(submission.total_ctc)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Internship Details */}
              {submission.placement_type === 'internship' && (
                <div className="mt-4 p-3 bg-background border border-border">
                  <h5 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    Internship Details
                  </h5>
                  <div className="space-y-1 text-sm font-mono">
                    <div className="flex justify-between">
                      <span>Monthly Stipend:</span>
                      <span>{formatCurrency(submission.internship_stipend)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Duration:</span>
                      <span>{submission.internship_duration} months</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Verification Layers */}
            <div className="space-y-4">
              <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Verification Layers
              </h4>

              {/* Verification Requirements Alert */}
              {submission.verification_status === 'pending' && (
                <div className={`p-3 border text-sm ${
                  verification_layers?.linkedin?.verified && verification_layers?.company_email?.verified
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-amber-50 border-amber-200 text-amber-700'
                }`}>
                  <p className="font-medium mb-1">Auto-Verification Requires:</p>
                  <ul className="space-y-1">
                    <li className="flex items-center gap-2">
                      {verification_layers?.company_email?.verified ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <XCircle className="h-3 w-3" />
                      )}
                      Company Email OTP
                    </li>
                    <li className="flex items-center gap-2">
                      {verification_layers?.linkedin?.verified ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <XCircle className="h-3 w-3" />
                      )}
                      LinkedIn (Company & Role)
                    </li>
                  </ul>
                </div>
              )}

              {/* LinkedIn */}
              <div className={`p-3 border ${verification_layers?.linkedin?.verified ? 'border-green-200 bg-green-50' : 'border-border bg-background'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Linkedin className={`h-4 w-4 ${verification_layers?.linkedin?.verified ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <span className="font-medium text-sm">LinkedIn Verification</span>
                  {verification_layers?.linkedin?.verified ? (
                    <CheckCircle className="h-4 w-4 text-green-600 ml-auto" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground ml-auto" />
                  )}
                </div>
                {verification_layers?.linkedin?.url ? (
                  <a 
                    href={verification_layers.linkedin.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    View Profile <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-sm text-muted-foreground">No LinkedIn URL provided</span>
                )}
                {verification_layers?.linkedin?.error && (
                  <p className="text-xs text-red-600 mt-1 flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    {verification_layers.linkedin.error}
                  </p>
                )}
              </div>

              {/* Company Email */}
              <div className={`p-3 border ${verification_layers?.company_email?.verified ? 'border-green-200 bg-green-50' : 'border-border bg-background'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Mail className={`h-4 w-4 ${verification_layers?.company_email?.verified ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <span className="font-medium text-sm">Company Email</span>
                  {verification_layers?.company_email?.verified ? (
                    <CheckCircle className="h-4 w-4 text-green-600 ml-auto" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground ml-auto" />
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  {verification_layers?.company_email?.email || "No company email provided"}
                </span>
              </div>

              {/* College Email */}
              <div className={`p-3 border ${verification_layers?.college_email?.verified ? 'border-green-200 bg-green-50' : 'border-border bg-background'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Mail className={`h-4 w-4 ${verification_layers?.college_email?.verified ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <span className="font-medium text-sm">College Email</span>
                  {verification_layers?.college_email?.verified ? (
                    <CheckCircle className="h-4 w-4 text-green-600 ml-auto" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground ml-auto" />
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  {verification_layers?.college_email?.email || "No college email provided"}
                </span>
              </div>

              {/* Offer Letter */}
              {submission.offer_letter_url && (
                <div className="p-3 border border-blue-200 bg-blue-50">
                  <div className="flex items-center gap-2 mb-2">
                    <FileCheck className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-sm">Offer Letter (PDF)</span>
                  </div>
                  <a
                    href={submission.offer_letter_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-700 underline hover:text-blue-900 break-all"
                    data-testid="view-offer-letter-link"
                  >
                    View offer letter
                  </a>
                </div>
              )}

              {/* Admin Notes */}
              {submission.admin_notes && (
                <div className="p-3 bg-amber-50 border border-amber-200">
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-700">Admin Notes</span>
                  <p className="text-sm mt-1">{submission.admin_notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons - Only show for pending submissions */}
          {submission.verification_status === 'pending' && (
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex flex-col md:flex-row gap-4">
                <Input
                  placeholder="Add admin notes (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="flex-1"
                  data-testid="admin-notes-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAction('verify')}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="verify-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileCheck className="h-4 w-4 mr-2" />}
                    Verify
                  </Button>
                  <Button
                    onClick={() => handleAction('reject')}
                    disabled={loading}
                    variant="destructive"
                    data-testid="reject-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileX className="h-4 w-4 mr-2" />}
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Admin Login Component
const AdminLogin = ({ onLoginSuccess }) => {
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Countdown tick for OTP resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown(s => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const handleRequestOTP = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (resendCooldown > 0) return;
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/admin/auth/request-otp`, { email });
      toast.success('OTP sent to your email');
      setStep('otp');
      setOtp('');
      setResendCooldown(60);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send OTP';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/admin/auth/verify-otp`, { email, otp });
      toast.success('Login successful');
      onLoginSuccess();
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid OTP';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-background sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-heading text-xl font-bold tracking-tight" data-testid="logo">
              TRANSPLACEMENT
            </Link>
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              <span className="font-medium text-sm uppercase tracking-wider">Admin Access</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] p-6">
        <div className="w-full max-w-md">
          <div className="border border-border bg-background p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
                Admin Login
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                {step === 'email' 
                  ? 'Enter your authorized admin email' 
                  : 'Enter the OTP sent to your email'}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* Email Step */}
            {step === 'email' && (
              <form onSubmit={handleRequestOTP} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Admin Email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    disabled={loading}
                    className="mt-1"
                    data-testid="admin-email-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !email}
                  data-testid="request-otp-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4 mr-2" />
                      Request Access Code
                    </>
                  )}
                </Button>
              </form>
            )}

            {/* OTP Step */}
            {step === 'otp' && (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div className="text-center mb-4">
                  <p className="text-sm text-muted-foreground">
                    Code sent to <span className="font-medium text-foreground">{email}</span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Access Code</label>
                  <Input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    disabled={loading}
                    className="mt-1 text-center text-2xl font-mono tracking-widest"
                    maxLength={6}
                    data-testid="admin-otp-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Code expires in 5 minutes
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || otp.length !== 6}
                  data-testid="verify-otp-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Verify & Login
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => handleRequestOTP()}
                  disabled={loading || resendCooldown > 0}
                  data-testid="admin-resend-otp-btn"
                >
                  {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => { setStep('email'); setOtp(''); setError(''); setResendCooldown(0); }}
                  disabled={loading}
                >
                  Use different email
                </Button>
              </form>
            )}

            {/* Security Notice */}
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-xs text-center text-muted-foreground">
                Access restricted to authorized administrators only.
                <br />
                Sessions expire after 30 minutes of inactivity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');
  const [colleges, setColleges] = useState([]);
  const [collegeFilter, setCollegeFilter] = useState('all');

  // Check admin session on mount
  useEffect(() => {
    checkAdminSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAdminSession = async () => {
    try {
      const response = await axios.get(`${API}/admin/auth/session`);
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        setAdminEmail(response.data.email);
        setSessionExpiry(response.data.expires_at);
        loadAdminData();
      }
    } catch (error) {
      console.error("Session check error:", error);
    } finally {
      setCheckingAuth(false);
    }
  };

  const loadAdminData = () => {
    fetchColleges();
    fetchStats();
    fetchSubmissions();
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    checkAdminSession();
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/auth/logout`, {});
      setIsAuthenticated(false);
      setAdminEmail('');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubmissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, collegeFilter, isAuthenticated]);

  const fetchColleges = async () => {
    try {
      const response = await axios.get(`${API}/colleges?limit=100`);
      setColleges(response.data);
    } catch (error) {
      console.error("Error fetching colleges:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
      }
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('verification_status', statusFilter);
      }
      if (collegeFilter && collegeFilter !== 'all') {
        params.append('college_id', collegeFilter);
      }
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/admin/submissions?${params.toString()}`);
      setSubmissions(response.data.submissions);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (placementId, notes) => {
    try {
      await axios.post(`${API}/admin/submissions/${placementId}/verify`, {
        placement_id: placementId,
        action: 'verify',
        admin_notes: notes || null
      });
      toast.success("Submission verified!");
      fetchSubmissions();
      fetchStats();
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        toast.error("Session expired. Please login again.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to verify submission");
      }
    }
  };

  const handleReject = async (placementId, notes) => {
    try {
      await axios.post(`${API}/admin/submissions/${placementId}/verify`, {
        placement_id: placementId,
        action: 'reject',
        admin_notes: notes || null
      });
      toast.success("Submission rejected");
      fetchSubmissions();
      fetchStats();
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        toast.error("Session expired. Please login again.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to reject submission");
      }
    }
  };

  // Show loading state while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Authenticated admin view
  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="border-b border-border bg-background sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-heading text-xl font-bold tracking-tight" data-testid="logo">
              TRANSPLACEMENT
            </Link>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="text-muted-foreground hidden md:inline">{adminEmail}</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">
            Submission Review
          </h1>
          <p className="text-muted-foreground mt-2">
            Review and verify student placement submissions
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="border border-border p-4 bg-background">
              <div className="text-2xl font-bold font-mono">{stats.total_submissions}</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider">Total</div>
            </div>
            <div className="border border-amber-200 bg-amber-50 p-4">
              <div className="text-2xl font-bold font-mono text-amber-700">{stats.pending}</div>
              <div className="text-sm text-amber-600 uppercase tracking-wider">Pending</div>
            </div>
            <div className="border border-green-200 bg-green-50 p-4">
              <div className="text-2xl font-bold font-mono text-green-700">{stats.verified}</div>
              <div className="text-sm text-green-600 uppercase tracking-wider">Verified</div>
            </div>
            <div className="border border-red-200 bg-red-50 p-4">
              <div className="text-2xl font-bold font-mono text-red-700">{stats.rejected}</div>
              <div className="text-sm text-red-600 uppercase tracking-wider">Rejected</div>
            </div>
          </div>
        )}

        {/* Pending Breakdown */}
        {stats?.pending_breakdown && stats.pending > 0 && (
          <div className="mb-8 p-4 border border-border bg-secondary">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
              Pending Submissions Breakdown
            </h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Linkedin className="h-4 w-4 text-sky-600" />
                <span className="text-sm">
                  <strong>{stats.pending_breakdown.with_linkedin_verified}</strong> with LinkedIn verified
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" />
                <span className="text-sm">
                  <strong>{stats.pending_breakdown.with_email_verified}</strong> with email verified
                </span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm">
                  <strong>{stats.pending_breakdown.no_verification}</strong> with no verification
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Select value={collegeFilter} onValueChange={setCollegeFilter}>
              <SelectTrigger className="h-10" data-testid="college-filter">
                <SelectValue placeholder="Filter by college" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Colleges</SelectItem>
                {colleges.map(college => (
                  <SelectItem key={college.college_id} value={college.college_id}>
                    {college.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            variant="outline" 
            onClick={() => { fetchSubmissions(); fetchStats(); }}
            data-testid="refresh-btn"
          >
            Refresh
          </Button>
        </div>

        {/* Submissions List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton h-24 w-full" />
            ))}
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-12 border border-border bg-secondary">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-lg font-bold uppercase">No Submissions Found</h3>
            <p className="text-muted-foreground mt-2">
              {statusFilter === 'pending' 
                ? 'No pending submissions to review' 
                : 'No submissions match your filters'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map(submission => (
              <SubmissionCard
                key={submission.placement_id}
                submission={submission}
                onVerify={handleVerify}
                onReject={handleReject}
                isExpanded={expandedId === submission.placement_id}
                onToggle={() => setExpandedId(
                  expandedId === submission.placement_id ? null : submission.placement_id
                )}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
